import { defineStore } from 'pinia'
import { graphql, type FragmentOf } from 'gql.tada'

const StaffFragment = graphql(`
  fragment Staff on Staff @_unmask {
    __typename
    id
    is_student
    full_name
    organisation {
      is_trial
      id
      subscription
    }
    school_classes {
      id
      slug
      name
    }
    roles {
      name
      label
    }
  }
`)
export type StaffType = FragmentOf<typeof StaffFragment>

const StudentFragment = graphql(`
  fragment Student on Student @_unmask {
    __typename
    created_at
    id
    is_student
    full_name
    first_name
    initials
    school_class {
      enrolment_year
    }
    organisation {
      is_trial
      id
    }
  }
`)
export type StudentType = FragmentOf<typeof StudentFragment>

export const useAuthStore = defineStore('auth', () => {
  const { $urql } = useNuxtApp()
  const router = useRouter()
  const route = useRoute()
  const { notify } = useNotification()
  const i18n = useI18n()

  const user: Ref<StaffType | StudentType | undefined> = ref()
  const student = computed(() => {
    if (user.value?.__typename !== 'Student') return undefined
    return user.value as StudentType
  })
  const staff = computed(() => {
    if (user.value?.__typename !== 'Staff') return undefined
    return user.value as StaffType
  })
  const isTrial = computed(() => {
    return user.value?.organisation.is_trial
  })

  const schoolClass: Ref<any | undefined> = ref(undefined)
  const initalized = ref(false)
  const initError = ref('')

  function updateClass() {
    if (route.params.class) {
      const lookUp = (user.value as StaffType).school_classes?.find(
        sc => sc.slug === route.params.class,
      )
      if (lookUp) {
        schoolClass.value = lookUp
        return
      }
    }
    schoolClass.value = (user.value as StaffType).school_classes?.[0]
  }

  const fetch = async () => {
    const result = await $urql.query(
      graphql(
        `
          query MeRefresh {
            me {
              ...Staff
              ...Student
            }
          }
        `,
        [StaffFragment, StudentFragment],
      ),
      {},
      {
        requestPolicy: 'network-only',
        additionalTypenames: ['Staff', 'Student'],
      },
    )

    if (!result.data?.me) {
      user.value = undefined
      return
    }

    if (result.data?.me?.__typename === 'Staff') {
      user.value = result.data.me as StaffType
      updateClass()
      initalized.value = true
    } else if (result.data?.me?.__typename === 'Student') {
      initalized.value = true
      user.value = result.data.me as StudentType
    } else {
      throw createError({
        message: i18n.t('errors.invalid_user_type'),
        statusCode: 401,
        fatal: true,
      })
    }
  }

  const init = async () => {
    if (initalized.value) return

    if (route.query.code) {
      router.replace({ query: {} })
      try {
        await loginStaff(route.query.code as string)
        return
      } catch (e: any) {
        console.error('init error', e)
        if (e.message) {
          initError.value = i18n.t(e.message)
        } else {
          useHandleError(e)
        }
      }
    }

    await fetch()
  }

  const loginStaff = async (code: string) => {
    const result = await $urql.mutation(
      graphql(
        `
          mutation LoginStaff($code: String!) {
            login_staff(code: $code) {
              ...Staff
            }
          }
        `,
        [StaffFragment],
      ),
      { code },
      {
        requestPolicy: 'network-only',
        additionalTypenames: ['Staff', 'Student'],
      },
    )

    if (result.error || !result.data?.login_staff) {
      throw createError({
        message:
          Object.values(
            result.error?.graphQLErrors[0]?.extensions?.validation ?? {},
          )
            .map(e => e[0])
            .map(e => i18n.t(e))
            .join('\n\n') ?? i18n.t('errors.account_cannot_logged_in'),
        statusCode: 401,
      })
    }

    user.value = result.data.login_staff as StaffType
    updateClass()
    initalized.value = true
  }

  const loginStudent = async (code: string) => {
    const result = await $urql.mutation(
      graphql(
        `
          mutation LoginStudent($code: String!) {
            student_login(login_code: $code) {
              ...Student
            }
          }
        `,
        [StudentFragment],
      ),
      {
        code,
      },
      { requestPolicy: 'network-only' },
    )
    useHandleError(result.error, true)

    if (result.data?.student_login?.__typename === 'Student') {
      user.value = result.data.student_login as StudentType
      initalized.value = true
    } else {
      throw createError({
        message: i18n.t('errors.invalid_code'),
        statusCode: 401,
      })
    }
  }

  const logout = async () => {
    await $urql.mutation(
      graphql(`
        mutation Logout {
          logout
        }
      `),
      {},
      {
        requestPolicy: 'network-only',
        additionalTypenames: ['Staff', 'Student'],
      },
    )
    router.push({ name: 'Login' })
    user.value = undefined
    initalized.value = false
    initError.value = ''
  }

  // redirect if class changes
  watch(schoolClass, (schoolClass, oldSchoolClass) => {
    // page doesn't require class
    if (!schoolClass) return

    // first time setting class
    if (!oldSchoolClass) return

    if (route.params.class === schoolClass.slug) return

    // only if page has an option for class
    const { matched } = router.currentRoute.value
    if (!matched[matched.length - 1]?.path.includes(':class')) {
      return
    }

    const classes = staff.value!.school_classes.length
    if (classes <= 1) router.push({ name: 'Dashboard' })
    else router.push({
      name: 'Dashboard',
      params: { class: schoolClass.slug },
    })
  })

  const homepage = computed(() => {
    if (!user.value) return { name: 'Login' }

    if (student.value) {
      return { name: 'StudentIndex' }
    }

    if (staff.value?.roles.find(role => role.name === 'teacher')) {
      const classes = staff.value.school_classes.length

      // no classes
      if (classes === 0) {
        if (staff.value?.roles.find(role => role.name === 'class-manager')) {
          return { name: 'ClassAdministration' }
        }
        return { name: 'AdminIndex' }
        // one class
      } else if (classes === 1) return { name: 'Dashboard' }
      // multiple classes
      else return {
        name: 'Dashboard',
        params: { class: schoolClass.value.slug },
      }
    }

    return { name: 'AdminIndex' }
  })

  // check if user is still logged in
  if (import.meta.client) {
    let lastBlurTime: number = 0
    const STALE_THRESHOLD = 5 * 60 * 1000 // 5 minutes

    useEventListener('blur', () => {
      lastBlurTime = Date.now()
    })

    useEventListener('focus', async () => {
      const now = Date.now()
      const timeAway = now - lastBlurTime

      if (timeAway > STALE_THRESHOLD && user.value) {
        await fetch()

        if (!user.value) {
          notify({
            type: 'info',
            title: i18n.t('errors.session_ended'),
            text: i18n.t('errors.finished_session'),
          })
          await router.push({ name: 'Login' })
        }
      }
    })
  }

  return {
    fetch,
    homepage,
    init,
    initalized,
    initError,
    isTrial,
    loginStaff,
    loginStudent,
    logout,
    schoolClass,
    user,
    staff,
    student,
  }
})
